import * as React from 'react';
import * as _ from 'underscore';
import { PLURAL_FUNCTIONS, SIMPLE_PLURAL_RULE } from './plural_rules.js';

var IV_TAG_REGEX = /\{\{#_iv\}\}(.+?)\{\{\/_iv\}\}/g;
var IVAR_TAG_REGEX = /\{\{#_ivar\}\}(.+?)\{\{\/_ivar\}\}/g;
var PLURALIZE_TAG_REGEX = /\{\{#pluralize\}\}(.+?)\{\{\/pluralize\}\}/g;
var VARIABLE_TAG_REGEX = /\{\{(\w+?)\}\}/g;
var HTML_TAG_REGEX = /<[a-z].*?>/i;
var RTL = (function () {
    if (document.dir) {
        return document.dir;
    }
    var dirAttr = document.documentElement.getAttribute("dir");
    if (dirAttr) {
        return dirAttr;
    }
    return "ltr";
})() === "rtl";
var getPluralFunction = function (language, error) {
    if (PLURAL_FUNCTIONS.hasOwnProperty(language)) {
        return PLURAL_FUNCTIONS[language];
    }
    // In case of error, default to singular string
    error("No plural rule for ".concat(language));
    return SIMPLE_PLURAL_RULE;
};
var stripHTMLTags = function (s) {
    if (!HTML_TAG_REGEX.test(s)) {
        return s;
    }
    try {
        // Try DOM-based method
        var container = document.createElement("div");
        container.innerHTML = s;
        while (container.firstElementChild) {
            container.removeChild(container.firstElementChild);
        }
        if (container.textContent === null) {
            throw Error();
        }
        return container.textContent;
    }
    catch (_a) {
        // Fall back to regex method (doesn't work on nested same tags, e.g.
        // '<b><b>Hello</b></b>'). See http://stackoverflow.com/a/24810194
        return s.replace(/<([^>]+?)([^>]*?)>(.*?)<\/\1>/gi, "");
    }
};
var convertUiLanguageToLanguageId = function (languageId) {
    if (languageId.startsWith("zh")) {
        return "zh";
    }
    return languageId;
};
var translate = function (localizationData, errorFunc, stringId, variables, options) {
    var _a, _b;
    if (variables === void 0) { variables = {}; }
    if (options === void 0) { options = {}; }
    // Get localization
    var maybeString = (_a = localizationData.getStrings()[stringId]) === null || _a === void 0 ? void 0 : _a[0];
    if (typeof maybeString === "undefined") {
        var maybeFallbackString = (_b = localizationData.getFallback()[stringId]) === null || _b === void 0 ? void 0 : _b[0];
        if (typeof maybeFallbackString === "undefined") {
            errorFunc("Missing localization for ".concat(stringId, "."));
            throw Error("Missing localization for ".concat(stringId, "."));
        }
        else {
            errorFunc("Could not find string ".concat(stringId, ", falling back to en text"));
            maybeString = maybeFallbackString;
        }
    }
    var localizedString = maybeString[1];
    // If in RTL mode, then add the right-to-left mark.
    // This is because some strings, such as `5 XP` are not
    // identified by the browser as possibly RTL even though
    // the top HTML tag is identified as dir=rtl
    if (RTL) {
        localizedString = "\u200F".concat(localizedString);
    }
    // Perform and custom preprocessing on the raw string template
    localizedString = (options.preprocess || (function (s) { return s; }))(localizedString);
    // Resolve {{#pluralize}} tags
    localizedString = localizedString.replace(PLURALIZE_TAG_REGEX, function (_a, pluralizeData) {
        var pieces;
        try {
            // Parse new-style pluralize tags
            pieces = JSON.parse(pluralizeData);
        }
        catch (_b) {
            // Parse old-style pluralize tags
            pieces = pluralizeData.split(",");
        }
        var quantity = variables[pieces.pop().slice(2, -2)];
        return pieces[getPluralFunction(convertUiLanguageToLanguageId(localizationData.uiLanguage), errorFunc)(quantity)];
    });
    // Convert {{#_ivar}} tags into {{#_iv}} + {{foo}}
    localizedString = localizedString.replace(IVAR_TAG_REGEX, function (_a, variable) { return "{{#_iv}}{{".concat(variable, "}}{{/_iv}}"); });
    // If we will dangerouslySetInnerHTML then variables must be manually
    // escaped. Otherwise, react will handle escaping.
    var dangerouslySetInnerHtml = HTML_TAG_REGEX.test(localizedString);
    // Resolve {{foo}} tags
    localizedString = localizedString.replace(VARIABLE_TAG_REGEX, function (_a, variable) {
        // Check that value is supplied
        if (!(variable in variables)) {
            errorFunc("Missing variable: ".concat(variable, " for string ID ").concat(stringId), {
                once: true,
            });
            return "";
        }
        // Check that value is printable
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        var value = variables[variable];
        if (typeof value !== "string" && typeof value !== "number") {
            errorFunc("Invalid variable value ".concat(value, " for: ").concat(variable, " for string ID ").concat(stringId));
            return "";
        }
        return dangerouslySetInnerHtml ? _.escape("".concat(value)) : "".concat(value);
    });
    // Resolve {{#_iv}} tags
    localizedString = localizedString.replace(IV_TAG_REGEX, function (_a, innerString) {
        var undeclaredStrings = localizationData.getUndeclared() || {};
        // Karin says that _ivar/_iv are only expected to handle the English names
        // of languages (e.g. 'French', 'Spanish||ALL_CAPS') and that other
        // variables (e.g. 'Strengthen {{#_ivar}}title{{/_ivar}}') should just
        // silently remain untranslated
        return innerString in undeclaredStrings
            ? undeclaredStrings[innerString]
            : innerString.split("||")[0]; // this is for English as it doesn't have undeclared strings
    });
    // Perform any custom postprocessing on the localized string
    var _c = options.postprocess, postprocess = _c === void 0 ? function (s) { return s; } : _c;
    if (options.stripTags) {
        localizedString = stripHTMLTags(localizedString);
    }
    localizedString = postprocess(localizedString);
    // Perform language specific postprocessing and sanitization
    if (localizationData.uiLanguage === "fr") {
        // Replace spaces before or after certain punctuation with non-breaking spaces
        localizedString = localizedString.replace(/(\u00AB) | ([?!:;\u00BB])/g, "$1\u00A0$2");
    }
    // Parse HTML
    // eslint-disable-next-line prefer-const
    var localizedNode = dangerouslySetInnerHtml ? (React.createElement("span", { dangerouslySetInnerHTML: { __html: localizedString } })) : (localizedString);
    return localizedNode;
};

export { convertUiLanguageToLanguageId, stripHTMLTags, translate };
